class SfCommunityVideos extends HTMLElement {
  constructor() {
    super();
    this.players = new Map();
    this.mutedState = new Map();
    this.observer = null;
    this.activePlayer = null;
    this._tiktokReady = new Set();
    this._tiktokQueue = new Map();
  }

  connectedCallback() {
    this.initMuteButtons();
    this.initIntersectionObserver();
    this.initVideoClicks();
    this.initNativeVideoPreviews();
    this.initTiktokMessages();

    const cards = this.querySelectorAll('[data-video-id]');
    cards.forEach((card) => {
      this.mutedState.set(card.dataset.videoId, true);
    });

    this._carousel = this.querySelector('.sf-ugc__carousel');
    if (this._carousel) {
      this._checkOverflow();
      this._resizeObserver = new ResizeObserver(() => this._checkOverflow());
      this._resizeObserver.observe(this._carousel);
      this._initDragScroll();
    }
  }

  initTiktokMessages() {
    this._onTiktokMessage = (event) => {
      const data = event.data;
      if (!data || typeof data !== 'object' || data['x-tiktok-player'] !== true) return;
      const iframes = this.querySelectorAll('[data-video-type="tiktok"] iframe');
      let card = null;
      iframes.forEach((iframe) => {
        if (iframe.contentWindow === event.source) {
          card = iframe.closest('[data-video-id]');
        }
      });
      if (!card) return;

      if (data.type === 'onPlayerReady') {
        this._tiktokReady.add(card.dataset.videoId);
        this._flushTiktokQueue(card);
      } else if (data.type === 'onStateChange') {
        // 1 = playing, 2 = paused, 0 = ended
        if (data.value === 1) {
          card.classList.add('is-playing');
        } else if (data.value === 2 || data.value === 0) {
          card.classList.remove('is-playing');
        }
      } else if (data.type === 'onMute') {
        this.mutedState.set(card.dataset.videoId, !!data.value);
        const btn = card.querySelector('[data-mute-btn]');
        if (btn) {
          if (data.value) {
            btn.classList.add('sf-ugc__mute-btn--muted');
          } else {
            btn.classList.remove('sf-ugc__mute-btn--muted');
          }
        }
      }
    };
    window.addEventListener('message', this._onTiktokMessage);
  }

  disconnectedCallback() {
    if (this._onTiktokMessage) {
      window.removeEventListener('message', this._onTiktokMessage);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
    this._resizeObserver?.disconnect();
    document.removeEventListener('mousemove', this._onDragMove);
    document.removeEventListener('mouseup', this._onDragEnd);
    this.players.clear();
    this.mutedState.clear();
  }

  _initDragScroll() {
    this._dragging = false;
    this._didDrag = false;
    this._dragStartX = 0;
    this._dragScrollStart = 0;

    this._onDragStart = (e) => {
      // Only left mouse button; ignore mute-btn so it still works
      if (e.button !== 0) return;
      if (e.target.closest('[data-mute-btn]')) return;
      this._dragging = true;
      this._didDrag = false;
      this._dragStartX = e.pageX;
      this._dragScrollStart = this._carousel.scrollLeft;
      this._carousel.classList.add('is-dragging');
      document.addEventListener('mousemove', this._onDragMove);
      document.addEventListener('mouseup', this._onDragEnd);
      e.preventDefault();
    };

    this._onDragMove = (e) => {
      if (!this._dragging) return;
      const dx = e.pageX - this._dragStartX;
      if (Math.abs(dx) > 5) this._didDrag = true;
      this._carousel.scrollLeft = this._dragScrollStart - dx;
    };

    this._onDragEnd = () => {
      if (!this._dragging) return;
      this._dragging = false;
      this._carousel.classList.remove('is-dragging');
      document.removeEventListener('mousemove', this._onDragMove);
      document.removeEventListener('mouseup', this._onDragEnd);
      // Reset didDrag after click handlers have fired so togglePlay is suppressed
      setTimeout(() => { this._didDrag = false; }, 0);
    };

    this._carousel.addEventListener('mousedown', this._onDragStart);

    // Suppress clicks that happen right after a real drag (prevents togglePlay)
    this._carousel.addEventListener('click', (e) => {
      if (this._didDrag) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  _checkOverflow() {
    const hasOverflow = this._carousel.scrollWidth > this._carousel.clientWidth + 1;
    this._carousel.classList.toggle('sf-ugc__carousel--no-scroll', !hasOverflow);
  }

  initMuteButtons() {
    const buttons = this.querySelectorAll('[data-mute-btn]');
    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const card = btn.closest('[data-video-id]');
        if (!card) return;
        this.toggleMute(card);
      });
    });
  }

  initVideoClicks() {
    const videoWrappers = this.querySelectorAll('.sf-ugc__video');
    videoWrappers.forEach((wrapper) => {
      wrapper.addEventListener('click', (e) => {
        if (e.target.closest('[data-mute-btn]')) return;
        const card = wrapper.closest('[data-video-id]');
        if (!card) return;
        this.togglePlay(card);
      });
    });
  }

  initNativeVideoPreviews() {
    const nativeVideos = this.querySelectorAll('.sf-ugc__card video');
    nativeVideos.forEach((videoElement) => {
      const video = /** @type {HTMLVideoElement} */ (videoElement);
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');

      const showFirstFrame = () => {
        try {
          if (video.currentTime < 0.05) video.currentTime = 0.1;
        } catch (err) { /* iOS may block until user gesture; #t=0.1 in src covers that */ }
      };

      video.addEventListener('loadedmetadata', showFirstFrame, { once: true });
      video.addEventListener('loadeddata', showFirstFrame, { once: true });
      video.load();
    });
  }

  initIntersectionObserver() {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          const iframe = card.querySelector('iframe');
          const nativeVideo = card.querySelector('video');
          if (!iframe && !nativeVideo) return;

          if (!entry.isIntersecting) {
            this.pauseVideo(card);
          }
        });
      },
      { threshold: 0.3 }
    );

    const cards = this.querySelectorAll('[data-video-id]');
    cards.forEach((card) => this.observer.observe(card));
  }

  getPlayer(card) {
    const videoId = card.dataset.videoId;
    if (this.players.has(videoId)) return this.players.get(videoId);

    const iframe = card.querySelector('iframe');
    if (!iframe || typeof Vimeo === 'undefined') return null;

    const player = new Vimeo.Player(iframe);
    this.players.set(videoId, player);
    return player;
  }

  isTiktok(card) {
    return card.dataset.videoType === 'tiktok';
  }

  _sendTiktokMessage(card, type, value) {
    const iframe = card.querySelector('iframe');
    if (!iframe || !iframe.contentWindow) return;
    const msg = { type, 'x-tiktok-player': true };
    if (typeof value !== 'undefined') msg.value = value;
    try {
      iframe.contentWindow.postMessage(msg, '*');
    } catch (err) {
      // ignore
    }
  }

  postTiktok(card, type, value) {
    const id = card.dataset.videoId;
    if (this._tiktokReady.has(id)) {
      this._sendTiktokMessage(card, type, value);
      return;
    }
    if (!this._tiktokQueue.has(id)) this._tiktokQueue.set(id, []);
    this._tiktokQueue.get(id).push({ type, value });
    // Best-effort retry in case onPlayerReady never arrives or arrived earlier
    this._sendTiktokMessage(card, type, value);
    clearTimeout(this._tiktokRetryTimers?.get(id));
    if (!this._tiktokRetryTimers) this._tiktokRetryTimers = new Map();
    this._tiktokRetryTimers.set(id, setTimeout(() => {
      if (!this._tiktokReady.has(id)) {
        const queue = this._tiktokQueue.get(id) || [];
        queue.forEach((cmd) => this._sendTiktokMessage(card, cmd.type, cmd.value));
      }
    }, 600));
  }

  _flushTiktokQueue(card) {
    const id = card.dataset.videoId;
    const queue = this._tiktokQueue.get(id);
    if (!queue || queue.length === 0) return;
    queue.forEach((cmd) => this._sendTiktokMessage(card, cmd.type, cmd.value));
    this._tiktokQueue.set(id, []);
  }

  async togglePlay(card) {
    if (this.isTiktok(card)) {
      const isPlaying = card.classList.contains('is-playing');
      if (isPlaying) {
        this.postTiktok(card, 'pause');
        card.classList.remove('is-playing');
        if (this.activePlayer === card) this.activePlayer = null;
      } else {
        if (this.activePlayer && this.activePlayer !== card) {
          await this.pauseVideo(this.activePlayer);
        }
        this.postTiktok(card, 'play');
        card.classList.add('is-playing');
        await this.setMuted(card, false);
        this.activePlayer = card;
      }
      return;
    }

    const nativeVideo = card.querySelector('video');
    if (nativeVideo) {
      try {
        if (nativeVideo.paused) {
          if (this.activePlayer && this.activePlayer !== card) {
            await this.pauseVideo(this.activePlayer);
          }
          await nativeVideo.play();
          card.classList.add('is-playing');
          this.setMuted(card, false);
          this.activePlayer = card;
        } else {
          nativeVideo.pause();
          card.classList.remove('is-playing');
          if (this.activePlayer === card) this.activePlayer = null;
        }
      } catch (err) {
        // Silently handle autoplay restrictions
      }
      return;
    }

    const player = this.getPlayer(card);
    if (!player) return;

    try {
      const paused = await player.getPaused();
      if (paused) {
        if (this.activePlayer && this.activePlayer !== card) {
          await this.pauseVideo(this.activePlayer);
        }
        await player.play();
        card.classList.add('is-playing');
        await this.setMuted(card, false);
        this.activePlayer = card;
      } else {
        await player.pause();
        card.classList.remove('is-playing');
        if (this.activePlayer === card) this.activePlayer = null;
      }
    } catch (err) {
      // Silently handle autoplay restrictions
    }
  }

  async pauseVideo(card) {
    if (this.isTiktok(card)) {
      this.postTiktok(card, 'pause');
      card.classList.remove('is-playing');
      if (this.activePlayer === card) this.activePlayer = null;
      return;
    }

    const nativeVideo = card.querySelector('video');
    if (nativeVideo) {
      nativeVideo.pause();
      card.classList.remove('is-playing');
      if (this.activePlayer === card) this.activePlayer = null;
      return;
    }

    const player = this.getPlayer(card);
    if (!player) return;

    try {
      await player.pause();
      card.classList.remove('is-playing');
      if (this.activePlayer === card) this.activePlayer = null;
    } catch (err) {
      // Silently handle
    }
  }

  async setMuted(card, muted) {
    const videoId = card.dataset.videoId;
    const btn = card.querySelector('[data-mute-btn]');
    const nativeVideo = card.querySelector('video');

    if (this.isTiktok(card)) {
      this.postTiktok(card, muted ? 'mute' : 'unMute');
      this.mutedState.set(videoId, muted);
    } else if (nativeVideo) {
      nativeVideo.muted = muted;
      this.mutedState.set(videoId, muted);
    } else {
      const player = this.getPlayer(card);
      if (!player) return;
      try {
        await player.setVolume(muted ? 0 : 1);
        this.mutedState.set(videoId, muted);
      } catch (err) {
        return;
      }
    }

    if (btn) {
      if (muted) {
        btn.setAttribute('aria-label', btn.dataset.unmuteLabel || 'Unmute');
        btn.classList.add('sf-ugc__mute-btn--muted');
      } else {
        btn.setAttribute('aria-label', btn.dataset.muteLabel || 'Mute');
        btn.classList.remove('sf-ugc__mute-btn--muted');
      }
    }
  }

  async toggleMute(card) {
    const videoId = card.dataset.videoId;
    const isMuted = this.mutedState.get(videoId);
    await this.setMuted(card, !isMuted);
  }
}

customElements.define('sf-community-videos', SfCommunityVideos);
